import Contact from '@repo/utils/models/Contact';
import Collection from '@repo/utils/models/Collection';
import type { ID, ReduxAction } from '@repo/utils/types';
import Occasion from '@repo/utils/helpers/Occasion';
import Duplicate from '@repo/utils/models/Duplicate';
import AppState from '../app-state';

export const selectDuplicatesCollection = (state: AppState) => state.data.duplicates;
export const selectScanInProgress = (state: AppState) => state.data.duplicates.isScanning;
export const selectDuplicate = (id: ID) => (state: AppState) =>
  state.data.duplicates.items.get(String(id));

export default class DuplicatesCollection extends Collection<Duplicate> {
  page: number;
  hasNext: boolean;
  isScanning: boolean;

  constructor(props: any = {}) {
    super(props);
    // instead of offset, archive endpoint uses page for pagination
    this.page = props.page || 1;
    this.hasNext = props.hasNext;
    this.isScanning = props.isScanning || false;
  }

  get canLoadMore(): boolean {
    return this.hasNext;
  }
}

export type DuplicatesState = DuplicatesCollection;
const initialDuplicatesState = new DuplicatesCollection();

export function duplicatesReducer(
  state: DuplicatesState = initialDuplicatesState,
  { type, payload }: ReduxAction,
) {
  const duplicateID = String(payload?.mergedDuplicateID);

  switch (type) {
    case 'FETCH_ALL_DUPLICATES_REQUESTED':
      return state.copyWith({ isFetching: state.isFetching });

    case 'FETCH_ALL_DUPLICATES_SUCCEEDED':
      const duplicateItems = payload?.response?.results.map((duplicateItem) => {
        duplicateItem.contacts = duplicateItem.contacts.map((contact) => new Contact(contact));
        return [String(duplicateItem.id), new Duplicate(duplicateItem)];
      });

      if (duplicateItems) {
        return state.copyWith({
          isFetching: false,
          updated: new Occasion(),
          persistedItemsOnServer: payload.response.total,
          hasNext: payload.response.has_next,
          page: payload.page,
          items: payload.page === 1 ? duplicateItems : state.items.merge(duplicateItems),
        });
      } else {
        return state.copyWith({ isFetching: false, updated: new Occasion() });
      }

    case 'DELETE_DUPLICATE':
      return state.copyWith({
        items: state.items.update(String(payload), (duplicate) =>
          duplicate.copyWith({ isTransitioningOut: true }),
        ),
      });

    case 'DELETE_DUPLICATE_MEMBER_PROFILE':
      const { contactID, id } = payload;
      const duplicate = state.items.get(String(id));
      const filteredDuplicate = duplicate.copyWith({
        contacts: duplicate.contacts.filter((contact) => contact.id != contactID),
      });

      if (filteredDuplicate.contacts.length < 2) {
        return state.copyWith({
          items: state.items.update(String(id), (_) =>
            filteredDuplicate.copyWith({ isTransitioningOut: true }),
          ),
        });
      }

      return state.copyWith({
        items: state.items.set(String(id), filteredDuplicate),
      });

    case 'SCAN_FOR_DUPLICATES':
    case 'SCAN_FOR_DUPLICATES_IN_PROGRESS':
    case 'CREATE_DUPLICATES_SCAN':
      return state.copyWith({
        isScanning: true,
      });

    case 'FETCH_DUPLICATES_SCAN_STATUS_SUCCEEDED':
      return state.copyWith({
        isScanning: payload.is_syncing,
      });

    case 'MERGE_CONTACT_BATCH_REQUESTED':
      const duplicateIsMerging = state.items.has(duplicateID);

      if (duplicateIsMerging) {
        return state.copyWith({
          items: state.items.update(duplicateID, (duplicate) =>
            duplicate?.copyWith({ isMerging: true }),
          ),
        });
      }

      return state;

    case 'MERGE_CONTACT_BATCH_SUCCEEDED':
      if (!!duplicateID) {
        return state.copyWith({
          items: state.items.update(duplicateID, (duplicate) =>
            duplicate?.copyWith({ isTransitioningOut: true }),
          ),
        });
      }

      return state.copyWith({
        items: state.items.map((duplicate) =>
          duplicate?.handleMergedContact(payload?.contactID, payload?.mergedContactIDs),
        ),
      });

    case 'DELETE_CONTACT_REQUESTED':
      return state.copyWith({
        items: state.items.map((duplicate) => duplicate?.handleArchivedContacts([payload.id])),
      });

    case 'DELETE_CONTACT_BATCH_REQUESTED':
      return state.copyWith({
        items: state.items.map((duplicate) =>
          duplicate?.handleArchivedContacts(payload.contact_ids),
        ),
      });

    case 'CLEAR_TRANSITIONING_OUT_DUPLICATES':
      return state.copyWith({
        items: state.items.filter((duplicate) => !duplicate?.isTransitioningOut),
      });

    default:
      return state;
  }
}
